import { apiService } from '../../../../components/api/apiService';
import { logInvokeProfiles } from './invokeProfilesLogger';

const CONNECTION = 'backend_py';
const BASE = '/api/v1/llm-gateway/invoke-profiles';

export type InvokeKind = 'chat_completions' | 'images_generations';

export type InvokeProfile = {
  id: string;
  name: string;
  match: string[];
  llm_id: string;
  invoke: InvokeKind;
  enabled: boolean;
};

export type InvokeProfileWrite = Omit<InvokeProfile, 'id'>;

function serviceErrorMessage(err: unknown): string {
  const axiosErr = err as {
    code?: string;
    response?: { data?: { error?: { message?: string }; message?: string }; status?: number };
    message?: string;
  };
  if (axiosErr.code === 'ERR_NETWORK' || axiosErr.message === 'Network Error') {
    return 'Cannot reach BackendPYApp. Start it from BackendPYApp.';
  }
  return (
    axiosErr.response?.data?.error?.message ||
    axiosErr.response?.data?.message ||
    axiosErr.message ||
    'Invoke profile request failed'
  );
}

function asInvoke(value: unknown): InvokeKind {
  return value === 'images_generations' ? 'images_generations' : 'chat_completions';
}

function asProfiles(data: unknown): InvokeProfile[] {
  const raw = data && typeof data === 'object' ? (data as { profiles?: unknown }).profiles : [];
  if (!Array.isArray(raw)) return [];
  const profiles: InvokeProfile[] = [];
  for (const item of raw) {
    const row = item && typeof item === 'object' ? (item as Record<string, unknown>) : {};
    const id = String(row.id || '');
    if (!id) continue;
    profiles.push({
      id,
      name: String(row.name || ''),
      match: Array.isArray(row.match) ? row.match.map((value) => String(value)) : [],
      llm_id: String(row.llm_id || ''),
      invoke: asInvoke(row.invoke),
      enabled: row.enabled !== false,
    });
  }
  return profiles;
}

export const invokeProfilesApi = {
  async list(): Promise<InvokeProfile[]> {
    try {
      const data = await apiService.get(BASE, {}, CONNECTION);
      const profiles = asProfiles(data);
      logInvokeProfiles('app', 'profiles_loaded', { count: profiles.length });
      return profiles;
    } catch (err) {
      const message = serviceErrorMessage(err);
      logInvokeProfiles('error', 'profiles_load_failed', { message });
      throw new Error(message);
    }
  },

  async save(profile: InvokeProfileWrite, id?: string): Promise<InvokeProfile[]> {
    const payload = {
      name: profile.name,
      match: profile.match,
      llm_id: profile.llm_id,
      invoke: profile.invoke,
      enabled: profile.enabled,
    };
    try {
      const data = id
        ? await apiService.put(`${BASE}/${id}`, payload, CONNECTION)
        : await apiService.post(BASE, payload, CONNECTION);
      const profiles = asProfiles(data);
      logInvokeProfiles('app', id ? 'profile_updated' : 'profile_created', { id });
      return profiles;
    } catch (err) {
      const message = serviceErrorMessage(err);
      logInvokeProfiles('error', 'profile_save_failed', { id, message });
      throw new Error(message);
    }
  },

  async remove(id: string): Promise<InvokeProfile[]> {
    try {
      const data = await apiService.delete(`${BASE}/${id}`, CONNECTION);
      const profiles = asProfiles(data);
      logInvokeProfiles('warning', 'profile_deleted', { id });
      return profiles;
    } catch (err) {
      const message = serviceErrorMessage(err);
      logInvokeProfiles('error', 'profile_delete_failed', { id, message });
      throw new Error(message);
    }
  },
};
